import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { normalizeError } from "../lib/normalizeError";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, ExternalLink, Check, X, Package, Truck, ShoppingCart, Ban } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// ── Helpers ──
const norm = (o) => ({
  id: o.order_id || o.id || "",
  item_sku: o.item_sku || "", item_name: o.item_name || "", item_id: o.item_id || "",
  status: o.status || "NEEDS_REVIEW",
  qty_requested: parseInt(o.qty_requested) || 0,
  qty_approved: parseInt(o.qty_approved) || 0,
  qty_ordered: parseInt(o.qty_ordered) || 0,
  qty_received: parseInt(o.qty_received) || 0,
  vendor_name: o.vendor_name || "", vendor_link: o.vendor_link || "",
  priority: o.priority || "NORMAL", po_number: o.po_number || "",
  notes: o.notes || "", created_at: o.created_at || "",
  created_by_email: o.created_by_email || "",
  approved_at: o.approved_at || "", ordered_at: o.ordered_at || "",
  received_at: o.received_at || "",
});

const TAB_CFG = [
  { key: "NEEDS_REVIEW", label: "Needs Review", filter: s => s === "NEEDS_REVIEW" || s === "PENDING", icon: ClipboardList },
  { key: "PENDING_ORDER", label: "Pending Order", filter: s => s === "PENDING_ORDER" || s === "APPROVED", icon: ShoppingCart },
  { key: "WAITING_DELIVERY", label: "Awaiting Delivery", filter: s => s === "WAITING_DELIVERY" || s === "ORDERED" || s === "PARTIALLY_RECEIVED", icon: Truck },
  { key: "CLOSED", label: "Closed", filter: s => s === "RECEIVED" || s === "DENIED" || s === "CANCELLED", icon: Ban },
];

const statusBadge = (s) => {
  const map = {
    NEEDS_REVIEW: "bg-yellow-900/30 text-yellow-400 border-yellow-900",
    PENDING: "bg-yellow-900/30 text-yellow-400 border-yellow-900",
    PENDING_ORDER: "bg-orange-900/30 text-orange-400 border-orange-900",
    APPROVED: "bg-orange-900/30 text-orange-400 border-orange-900",
    WAITING_DELIVERY: "bg-blue-900/30 text-blue-400 border-blue-900",
    ORDERED: "bg-blue-900/30 text-blue-400 border-blue-900",
    PARTIALLY_RECEIVED: "bg-cyan-900/30 text-cyan-400 border-cyan-900",
    RECEIVED: "bg-green-900/30 text-green-400 border-green-900",
    DENIED: "bg-red-900/30 text-red-400 border-red-900",
    CANCELLED: "bg-red-900/30 text-red-400 border-red-900",
  };
  return <Badge className={map[s] || ""}>{(s || "").replace("_", " ")}</Badge>;
};

export default function OrderQueue() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("NEEDS_REVIEW");
  const [modal, setModal] = useState(null); // { type, order }
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.roles?.includes("admin") || user?.roles?.includes("owner");

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders`);
      setOrders((res.data || []).map(norm));
    } catch { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const transition = async (orderId, body) => {
    setBusy(true);
    try {
      await axios.put(`${API}/orders/${orderId}`, body);
      toast.success(`Order updated`);
      setModal(null);
      fetchOrders();
    } catch (e) {
      toast.error(normalizeError(e.response?.data) || "Failed to update order");
    } finally { setBusy(false); }
  };

  const openModal = (type, order) => {
    const defaults = {
      approve: { qty_approved: order.qty_requested },
      markOrdered: { qty_ordered: order.qty_approved || order.qty_requested, po_number: order.po_number, vendor_name: order.vendor_name, vendor_link: order.vendor_link },
      receive: { qty_received_delta: (order.qty_ordered || order.qty_requested) - order.qty_received },
      deny: {},
      cancel: {},
    };
    setForm(defaults[type] || {});
    setModal({ type, order });
  };

  const filtered = orders.filter(o => TAB_CFG.find(t => t.key === activeTab)?.filter(o.status));
  const grouped = filtered.reduce((a, o) => { const k = o.vendor_name || "No Supplier"; (a[k] = a[k] || []).push(o); return a; }, {});

  const tabCounts = TAB_CFG.map(t => ({ ...t, count: orders.filter(o => t.filter(o.status)).length }));

  // ── Column config per tab ──
  const colsForTab = (tab) => {
    const base = [
      { key: "item_sku", label: "SKU", cls: "font-mono text-sm" },
      { key: "item_name", label: "Item", cls: "font-medium" },
    ];
    if (tab === "NEEDS_REVIEW") return [...base,
      { key: "qty_requested", label: "Qty Req", cls: "text-right font-mono" },
      { key: "priority", label: "Priority" },
      { key: "created_at", label: "Created", fmt: "date" },
    ];
    if (tab === "PENDING_ORDER") return [...base,
      { key: "qty_approved", label: "Qty Appr", cls: "text-right font-mono text-primary" },
      { key: "approved_at", label: "Approved", fmt: "date" },
    ];
    if (tab === "WAITING_DELIVERY") return [...base,
      { key: "qty_ordered", label: "Qty Ord", cls: "text-right font-mono" },
      { key: "qty_received", label: "Qty Rcv", cls: "text-right font-mono text-green-400" },
      { key: "po_number", label: "PO#" },
      { key: "status", label: "Status", fmt: "badge" },
    ];
    return [...base,
      { key: "status", label: "Status", fmt: "badge" },
      { key: "notes", label: "Notes", cls: "text-xs text-muted-foreground max-w-[200px] truncate" },
    ];
  };

  const renderCell = (order, col) => {
    const val = order[col.key];
    if (col.fmt === "date") return <span className="text-muted-foreground text-sm">{val ? new Date(val).toLocaleDateString() : "-"}</span>;
    if (col.fmt === "badge") return statusBadge(val);
    return <span className={col.cls || ""}>{val ?? "-"}</span>;
  };

  // ── Actions per tab ──
  const renderActions = (order) => {
    if (!isAdmin) return null;
    const tab = activeTab;
    if (tab === "NEEDS_REVIEW") return (
      <div className="flex gap-1.5">
        <Button size="sm" className="h-7 text-xs uppercase tracking-wider rounded-sm" onClick={() => openModal("approve", order)} data-testid={`approve-${order.id}`}>
          <Check className="w-3 h-3 mr-1" /> Approve
        </Button>
        <Button size="sm" variant="destructive" className="h-7 text-xs uppercase tracking-wider rounded-sm" onClick={() => openModal("deny", order)} data-testid={`deny-${order.id}`}>
          <X className="w-3 h-3 mr-1" /> Deny
        </Button>
      </div>
    );
    if (tab === "PENDING_ORDER") return (
      <div className="flex gap-1.5">
        <Button size="sm" className="h-7 text-xs uppercase tracking-wider rounded-sm" onClick={() => openModal("markOrdered", order)} data-testid={`mark-ordered-${order.id}`}>
          <ShoppingCart className="w-3 h-3 mr-1" /> Mark Ordered
        </Button>
        <Button size="sm" variant="destructive" className="h-7 text-xs uppercase tracking-wider rounded-sm" onClick={() => openModal("cancel", order)} data-testid={`cancel-${order.id}`}>
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
    if (tab === "WAITING_DELIVERY") return (
      <div className="flex gap-1.5">
        <Button size="sm" className="h-7 text-xs uppercase tracking-wider rounded-sm bg-green-700 hover:bg-green-600" onClick={() => openModal("receive", order)} data-testid={`receive-${order.id}`}>
          <Package className="w-3 h-3 mr-1" /> Receive
        </Button>
        <Button size="sm" variant="destructive" className="h-7 text-xs uppercase tracking-wider rounded-sm" onClick={() => openModal("cancel", order)} data-testid={`cancel-${order.id}`}>
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
    return null;
  };

  const cols = colsForTab(activeTab);

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl tracking-tight">ORDER QUEUE</h1>
            <p className="text-xs text-muted-foreground">Full procurement lifecycle</p>
          </div>
        </div>
        <Badge variant="outline" className="font-mono">{tabCounts[0].count} pending review</Badge>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="border-b border-border px-6 bg-card">
          <TabsList className="h-12 bg-transparent border-0 p-0 gap-6">
            {tabCounts.map(t => (
              <TabsTrigger key={t.key} value={t.key}
                className="h-12 px-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary rounded-none bg-transparent uppercase tracking-wider text-sm"
                data-testid={`tab-${t.key.toLowerCase()}`}>
                {t.label} ({t.count})
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-6 space-y-6">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : Object.keys(grouped).length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No orders in this stage</p>
              </div>
            ) : (
              Object.entries(grouped).map(([supplier, rows]) => (
                <div key={supplier} className="industrial-card overflow-hidden">
                  <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between">
                    <h3 className="font-heading text-lg tracking-tight">{supplier}</h3>
                    <Badge variant="outline" className="font-mono text-xs">{rows.length}</Badge>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="industrial-table-header">
                        {cols.map(c => <TableHead key={c.key} className={c.cls?.includes("text-right") ? "text-right" : ""}>{c.label}</TableHead>)}
                        {isAdmin && <TableHead className="w-48">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map(o => (
                        <TableRow key={o.id} className="industrial-table-row" data-testid={`order-row-${o.id}`}>
                          {cols.map(c => <TableCell key={c.key} className={c.cls?.includes("text-right") ? "text-right" : ""}>{renderCell(o, c)}</TableCell>)}
                          {isAdmin && <TableCell>{renderActions(o)}</TableCell>}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </Tabs>

      {/* ── Modals ── */}
      <Dialog open={!!modal} onOpenChange={open => !open && setModal(null)}>
        <DialogContent className="bg-card border-border">
          {modal?.type === "approve" && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-xl tracking-tight">APPROVE ORDER</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-3 bg-muted/30 rounded-sm">
                  <p className="font-medium">{modal.order.item_name}</p>
                  <p className="font-mono text-sm text-muted-foreground">{modal.order.item_sku}</p>
                  {!modal.order.vendor_name && <p className="text-xs text-yellow-400 mt-1">No supplier set.</p>}
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Approved Quantity</Label>
                  <Input type="number" min="1" value={form.qty_approved} onChange={e => setForm({ ...form, qty_approved: parseInt(e.target.value) || 0 })}
                    className="industrial-input font-mono text-lg mt-1" data-testid="approve-qty-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setModal(null)} className="uppercase tracking-wider rounded-sm">Cancel</Button>
                <Button disabled={busy} onClick={() => transition(modal.order.id, { status: "PENDING_ORDER", qty_approved: form.qty_approved })}
                  className="uppercase tracking-wider rounded-sm" data-testid="confirm-approve-btn">{busy ? "..." : "Approve"}</Button>
              </DialogFooter>
            </>
          )}

          {modal?.type === "markOrdered" && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-xl tracking-tight">MARK AS ORDERED</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-3 bg-muted/30 rounded-sm">
                  <p className="font-medium">{modal.order.item_name}</p>
                  <p className="font-mono text-sm text-muted-foreground">{modal.order.item_sku}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Qty to Order</Label>
                    <Input type="number" min="1" value={form.qty_ordered} onChange={e => setForm({ ...form, qty_ordered: parseInt(e.target.value) || 0 })}
                      className="industrial-input font-mono mt-1" data-testid="order-qty-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">PO Number</Label>
                    <Input value={form.po_number || ""} onChange={e => setForm({ ...form, po_number: e.target.value })}
                      className="industrial-input font-mono mt-1" placeholder="Optional" data-testid="po-number-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Vendor Name</Label>
                    <Input value={form.vendor_name || ""} onChange={e => setForm({ ...form, vendor_name: e.target.value })}
                      className="industrial-input mt-1" data-testid="vendor-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Vendor Link</Label>
                    <Input value={form.vendor_link || ""} onChange={e => setForm({ ...form, vendor_link: e.target.value })}
                      className="industrial-input mt-1" placeholder="https://..." data-testid="vendor-link-input" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setModal(null)} className="uppercase tracking-wider rounded-sm">Cancel</Button>
                <Button disabled={busy} onClick={() => transition(modal.order.id, {
                  status: "WAITING_DELIVERY", qty_ordered: form.qty_ordered,
                  po_number: form.po_number, vendor_name: form.vendor_name, vendor_link: form.vendor_link,
                })} className="uppercase tracking-wider rounded-sm" data-testid="confirm-order-btn">{busy ? "..." : "Mark Ordered"}</Button>
              </DialogFooter>
            </>
          )}

          {modal?.type === "receive" && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-xl tracking-tight text-green-400">RECEIVE DELIVERY</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-3 bg-green-950/30 border border-green-900/50 rounded-sm">
                  <p className="font-medium">{modal.order.item_name}</p>
                  <p className="font-mono text-sm text-muted-foreground">{modal.order.item_sku}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span>Ordered: <span className="font-mono text-primary">{modal.order.qty_ordered}</span></span>
                    <span>Already Received: <span className="font-mono text-green-400">{modal.order.qty_received}</span></span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Quantity Receiving Now</Label>
                  <Input type="number" min="1" value={form.qty_received_delta}
                    onChange={e => setForm({ ...form, qty_received_delta: parseInt(e.target.value) || 0 })}
                    className="industrial-input font-mono text-lg mt-1" data-testid="receive-qty-input" />
                  <p className="text-xs text-muted-foreground mt-1">
                    This will add to inventory on-hand and update order received total.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setModal(null)} className="uppercase tracking-wider rounded-sm">Cancel</Button>
                <Button disabled={busy} className="uppercase tracking-wider rounded-sm bg-green-700 hover:bg-green-600"
                  onClick={() => transition(modal.order.id, { qty_received_delta: form.qty_received_delta })}
                  data-testid="confirm-receive-btn">{busy ? "..." : "Confirm Receive"}</Button>
              </DialogFooter>
            </>
          )}

          {(modal?.type === "deny" || modal?.type === "cancel") && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl tracking-tight text-red-400">
                  {modal.type === "deny" ? "DENY ORDER" : "CANCEL ORDER"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-3 bg-red-950/30 border border-red-900/50 rounded-sm">
                  <p className="font-medium">{modal.order.item_name}</p>
                  <p className="font-mono text-sm text-muted-foreground">{modal.order.item_sku}</p>
                  <p className="text-sm text-red-400 mt-2">This action cannot be easily undone.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setModal(null)} className="uppercase tracking-wider rounded-sm">Go Back</Button>
                <Button variant="destructive" disabled={busy}
                  onClick={() => transition(modal.order.id, { status: modal.type === "deny" ? "DENIED" : "CANCELLED" })}
                  className="uppercase tracking-wider rounded-sm" data-testid="confirm-deny-btn">
                  {busy ? "..." : (modal.type === "deny" ? "Confirm Deny" : "Confirm Cancel")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
